import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Upload, ArrowLeft, Loader, CheckCircle2, X, Film } from "lucide-react";
import { uploadGameAuto } from "@/lib/chunkedUpload";

// MAX_BYTES limit removed — uploads stream directly to R2, which handles
// objects up to 5 TB. Frontend no longer client-side rejects on size.
const ALLOWED = ["video/mp4", "video/quicktime"];

const TeamSearchSelect = ({ api, value, onChange, disabled, placeholder, emptyLabel, testId, excludeId }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selectedName, setSelectedName] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const requestRef = useRef(0);
  const listId = `${testId}-results`;

  useEffect(() => {
    const requestId = ++requestRef.current;
    const timer = setTimeout(() => {
      setLoading(true);
      api().get("/teams", { params: { q: query.trim() || undefined, limit: 50 } })
        .then(r => {
          if (requestRef.current !== requestId) return;
          setResults((r.data || []).filter(team => team.id !== excludeId));
        })
        .catch(() => {
          if (requestRef.current === requestId) setResults([]);
        })
        .finally(() => {
          if (requestRef.current === requestId) setLoading(false);
        });
    }, query ? 180 : 0);
    return () => clearTimeout(timer);
  }, [api, query, excludeId]);

  useEffect(() => {
    if (!value) setSelectedName("");
  }, [value]);

  const choose = (team) => {
    onChange(team?.id || "");
    setSelectedName(team?.name || "");
    setQuery("");
    setOpen(false);
  };

  const displayValue = selectedName || query;
  return (
    <div
      style={{ position: 'relative', marginTop: 2 }}
      onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
      }}
    >
      <input
        data-testid={testId}
        className="input-field"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId}
        autoComplete="off"
        value={displayValue}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={e => {
          if (value) onChange("");
          setSelectedName("");
          setQuery(e.target.value);
          setOpen(true);
        }}
        onKeyDown={e => {
          if (e.key === "Escape") setOpen(false);
        }}
      />
      {open && !disabled && (
        <div
          id={listId}
          role="listbox"
          style={{
            position: 'absolute', zIndex: 30, top: 'calc(100% + 4px)', left: 0, right: 0,
            maxHeight: 240, overflowY: 'auto', padding: 4,
            background: '#111217', border: '1px solid var(--dark-border)', borderRadius: 10,
            boxShadow: '0 16px 36px rgba(0,0,0,0.45)',
          }}
        >
          {emptyLabel && (
            <button
              type="button"
              role="option"
              aria-selected={!value}
              onMouseDown={e => e.preventDefault()}
              onClick={() => choose(null)}
              style={{ width: '100%', padding: '9px 10px', border: 0, borderRadius: 7, background: 'transparent', color: 'var(--text-secondary)', textAlign: 'left', cursor: 'pointer', fontSize: 12 }}
            >{emptyLabel}</button>
          )}
          {results.map(team => (
            <button
              key={team.id}
              type="button"
              role="option"
              aria-selected={team.id === value}
              onMouseDown={e => e.preventDefault()}
              onClick={() => choose(team)}
              style={{ width: '100%', padding: '9px 10px', border: 0, borderRadius: 7, background: team.id === value ? 'rgba(0,229,255,0.12)' : 'transparent', color: '#fff', textAlign: 'left', cursor: 'pointer', fontSize: 13 }}
            >{team.name}</button>
          ))}
          {!loading && results.length === 0 && (
            <p style={{ padding: '9px 10px', color: 'var(--text-secondary)', fontSize: 12 }}>
              No teams found{query ? ` for “${query}”` : ""}.
            </p>
          )}
          {loading && <p style={{ padding: '9px 10px', color: 'var(--text-secondary)', fontSize: 12 }}>Searching…</p>}
        </div>
      )}
    </div>
  );
};

const GameUploadPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [recentGames, setRecentGames] = useState([]); // games this user uploaded that can have segments appended
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    title: "", description: "", team_id: "", away_team_id: "", opponent: "", game_date: "",
  });
  // Multi-part game support
  const [mode, setMode] = useState("full"); // "full" | "multi"
  const [segmentLabel, setSegmentLabel] = useState("Q1");
  const [parentGameId, setParentGameId] = useState(""); // empty = create new multi-part game; non-empty = append to existing
  const [duration, setDuration] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [completedGame, setCompletedGame] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (user?.role !== "ysv_admin" && user?.role !== "gdo") {
      navigate("/games", { replace: true });
      return;
    }
    // Pull recent games so they can pick one to append a segment to.
    // We used to filter to ONLY the current user's uploads — that hid most
    // of the games the user is allowed to extend (a YSV admin or league coach
    // who didn't personally upload but should still be able to add segments).
    // The backend already scopes /games to what this user is allowed to see,
    // so any returned game is a valid candidate.
    api().get("/games").then(r => {
      const all = r.data || [];
      // Most-recent first; backend already sorts by created_at desc.
      const recents = all.slice(0, 30);
      setRecentGames(recents);
      // Deep-link: ?parent=<game_id> wins over auto-detect
      const parentId = searchParams.get("parent");
      if (parentId && all.some(g => g.id === parentId)) {
        setMode("multi");
        setParentGameId(parentId);
        return;
      }
      // Auto-detect: most-recent unlocked multi-part game I personally uploaded.
      // Skip if user explicitly requested a fresh upload via ?fresh=1.
      if (searchParams.get("fresh")) return;
      const openGame = recents.find(g => g.is_multi_part && !g.multi_part_locked && g.uploaded_by_user_id === user.id);
      if (openGame) {
        setMode("multi");
        setParentGameId(openGame.id);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, user, navigate]);

  const readDuration = (f) => new Promise((resolve) => {
    const url = URL.createObjectURL(f);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(Math.round(v.duration)); };
    v.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
    v.src = url;
  });

  const onPickFile = async (e) => {
    setError(null);
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      setError(`Unsupported file type: ${f.type}. Use MP4 or MOV.`);
      return;
    }
    setFile(f);
    const d = await readDuration(f);
    setDuration(d);
    if (!form.title) setForm(s => ({ ...s, title: f.name.replace(/\.[^/.]+$/, "") }));
  };

  const submit = async () => {
    setError(null);
    if (!file) { setError("Pick a video file first"); return; }
    // When APPENDING a segment to an existing game, we don't need the metadata fields —
    // those come from the parent game. Only require segment_label + file.
    if (mode === "multi" && parentGameId) {
      if (!segmentLabel) { setError("Pick which part this video is (Q1/Q2/H1/etc.)"); return; }
    } else {
      if (!form.title.trim()) { setError("Title is required"); return; }
      if (!form.team_id) { setError("Choose the team this game belongs to"); return; }
      if (mode === "multi" && !segmentLabel) { setError("Pick which part this video is (Q1/Q2/H1/etc.)"); return; }
    }
    setUploading(true);
    setProgress(0);
    try {
      // For append-to-parent flow, copy parent's metadata into the upload meta so
      // the backend's R2PresignInput requires aren't violated.
      let metaBase;
      if (mode === "multi" && parentGameId) {
        const parent = recentGames.find(g => g.id === parentGameId);
        metaBase = {
          title: parent?.title || "Game segment",
          description: "",
          team_id: parent?.team_id || form.team_id,
          away_team_id: parent?.away_team_id || null,
          opponent: parent?.opponent || "",
          game_date: parent?.game_date || form.game_date,
        };
      } else {
        metaBase = {
          title: form.title.trim(),
          description: form.description.trim(),
          team_id: form.team_id,
          away_team_id: form.away_team_id || null,
          opponent: form.opponent.trim(),
          game_date: form.game_date,
        };
      }
      const game = await uploadGameAuto(
        file,
        {
          ...metaBase,
          duration_seconds: duration || 0,
          segment_label: mode === "multi" ? segmentLabel : null,
          parent_game_id: mode === "multi" && parentGameId ? parentGameId : null,
        },
        (pct) => setProgress(pct),
      );
      setCompletedGame(game);
      setSuccess(true);
      // For multi-part flows we hold the success screen and offer "Upload next part"
      // For full-game uploads we go straight to the video viewer.
      if (mode !== "multi") {
        setTimeout(() => navigate(`/games/${game.id}`), 1100);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Upload failed");
      setUploading(false);
    }
  };

  const uploadNextPart = () => {
    // Stay on the page, keep the parent game id, reset file + label so user can
    // pick the next segment.
    setParentGameId(completedGame?.id || "");
    setSegmentLabel("");
    setFile(null);
    setDuration(null);
    setProgress(0);
    setSuccess(false);
    setUploading(false);
    setCompletedGame(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const usedSegmentLabels = React.useMemo(() => {
    if (parentGameId) {
      const parent = recentGames.find(g => g.id === parentGameId);
      return new Set((parent?.segments || []).map(s => (s.label || "").toLowerCase()));
    }
    return new Set();
  }, [parentGameId, recentGames]);

  // Same overlap logic as the backend (kept simple — JS-side mirror so the user
  // sees disabled buttons immediately, the backend still enforces).
  const HALF_BLOCKS_Q = { h1: ["q1", "q2"], h2: ["q3", "q4"] };
  const Q_BLOCKS_HALF = { q1: "h1", q2: "h1", q3: "h2", q4: "h2" };
  const disabledLabels = React.useMemo(() => {
    const blocked = new Set();
    for (const lab of usedSegmentLabels) {
      // If a half was used, block its quarters
      if (HALF_BLOCKS_Q[lab]) HALF_BLOCKS_Q[lab].forEach(q => blocked.add(q));
      // If a quarter was used, block its half
      if (Q_BLOCKS_HALF[lab]) blocked.add(Q_BLOCKS_HALF[lab]);
    }
    // Also block any label that's already exactly used
    for (const lab of usedSegmentLabels) blocked.add(lab);
    return blocked;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usedSegmentLabels]);

  const segmentOptions = ["Q1", "Q2", "Q3", "Q4", "H1", "H2", "OT"];

  // Lock state — disable upload UI entirely if the parent game has been marked complete
  const parentGame = parentGameId ? recentGames.find(g => g.id === parentGameId) : null;
  const parentLocked = parentGame?.multi_part_locked === true;

  const markComplete = async () => {
    if (!completedGame?.id) return;
    if (!window.confirm("Mark this game as complete? No more segments can be added until you reopen it.")) return;
    try {
      const r = await api().post(`/games/${completedGame.id}/multi-part/complete`);
      setCompletedGame(r.data);
      // Land user on the watch page since the game is now complete
      setTimeout(() => navigate(`/games/${r.data.id}`), 600);
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't mark complete");
    }
  };

  return (
    <div data-testid="game-upload-page" style={{ padding: '24px 28px', maxWidth: 720, margin: '0 auto' }}>
      <button
        data-testid="game-upload-back"
        onClick={() => navigate("/games")}
        style={{
          background: 'none', border: 'none', color: 'var(--text-secondary)',
          cursor: 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6,
          marginBottom: 16, fontFamily: "'DM Sans', sans-serif",
        }}
      ><ArrowLeft size={14} /> Back to Games</button>

      <div style={{ marginBottom: 22 }}>
        <p style={{ fontSize: 11, color: '#ff6b00', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase' }}>YSV Super Admin</p>
        <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 28, fontWeight: 800, color: '#fff', marginTop: 4 }}>Upload a Game</h1>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6 }}>Upload the full game video (MP4 / MOV, no size limit). After upload it's instantly available to the assigned team.</p>
      </div>

      {success ? (
        <div data-testid="game-upload-success" className="glass-card" style={{ padding: 36, textAlign: 'center' }}>
          <CheckCircle2 size={56} style={{ color: '#4caf50', marginBottom: 14 }} />
          <h3 style={{ fontSize: 20, fontWeight: 800, marginBottom: 6, fontFamily: "'Outfit', sans-serif" }}>
            {mode === "multi" ? "Segment uploaded!" : "Game uploaded!"}
          </h3>
          {mode === "multi" && completedGame && (
            <>
              <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 18 }}>
                {(completedGame.segments || []).length} segment{(completedGame.segments || []).length === 1 ? "" : "s"} so far · {((completedGame.segments || []).map(s => s.label).join(" · ")) || "Part 1"}
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                {!completedGame.multi_part_locked && (
                  <button data-testid="upload-next-part" onClick={uploadNextPart} className="btn-primary">
                    Upload next part
                  </button>
                )}
                <button
                  data-testid="upload-mark-complete"
                  onClick={markComplete}
                  disabled={completedGame.multi_part_locked}
                  style={{
                    padding: '12px 22px',
                    background: completedGame.multi_part_locked ? 'rgba(76,175,80,0.25)' : '#4caf50',
                    border: '1px solid rgba(76,175,80,0.6)', borderRadius: 10,
                    color: '#fff', fontSize: 14, fontWeight: 800, cursor: completedGame.multi_part_locked ? 'default' : 'pointer',
                    fontFamily: "'Outfit', sans-serif",
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}
                >
                  {completedGame.multi_part_locked ? "Game complete ✓" : "Mark game complete"}
                </button>
                <button
                  data-testid="upload-finish"
                  onClick={() => navigate(`/games/${completedGame.id}`)}
                  style={{
                    padding: '12px 22px', background: 'transparent',
                    border: '1px solid var(--dark-border)', borderRadius: 10,
                    color: '#fff', fontWeight: 700, cursor: 'pointer',
                    fontFamily: "'Outfit', sans-serif",
                  }}
                >Done — watch game</button>
              </div>
              <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 16, lineHeight: 1.5 }}>
                Tip: clicking "Mark game complete" locks the game so no more segments can be added. You can re-open it later from the game's watch page if you missed a part.
              </p>
            </>
          )}
          {mode !== "multi" && <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Redirecting you to watch…</p>}
        </div>
      ) : (
        <div className="glass-card" style={{ padding: 24 }}>
          {/* Game type — Full Game vs Multi-Part */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Upload type</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[
                { key: "full", label: "Full Game (single file)", help: "One large video. Best for short games or strong WiFi." },
                { key: "multi", label: "Multi-Part (Q1 / Q2 / H1 / H2)", help: "Split into halves or quarters. Uploads faster on weak internet." },
              ].map(opt => (
                <button
                  key={opt.key}
                  type="button"
                  data-testid={`game-upload-mode-${opt.key}`}
                  onClick={() => { setMode(opt.key); setParentGameId(""); }}
                  disabled={uploading}
                  style={{
                    flex: '1 1 220px', padding: '12px 14px', textAlign: 'left', cursor: 'pointer',
                    background: mode === opt.key ? 'rgba(0,229,255,0.12)' : 'transparent',
                    border: '1px solid ' + (mode === opt.key ? 'rgba(0,229,255,0.5)' : 'var(--dark-border)'),
                    borderRadius: 12, color: '#fff', fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  <p style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{opt.label}</p>
                  <p style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.4 }}>{opt.help}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Multi-part settings */}
          {mode === "multi" && (
            <div style={{ marginBottom: 18, padding: 14, background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.18)', borderRadius: 12 }}>
              {parentGameId && (
                <div data-testid="parent-game-banner" style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                  background: 'rgba(0,229,255,0.12)', border: '1px solid rgba(0,229,255,0.45)',
                  borderRadius: 10, padding: '10px 14px', marginBottom: 14,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 11, color: 'var(--cyan)', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 2 }}>Adding to existing game</p>
                    <p style={{ fontSize: 13, color: '#fff', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {(recentGames.find(g => g.id === parentGameId) || {}).title || "(game)"}
                      {(recentGames.find(g => g.id === parentGameId) || {}).segments?.length
                        ? ` · ${(recentGames.find(g => g.id === parentGameId) || {}).segments.length} part(s) uploaded`
                        : ""}
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid="start-fresh-game-btn"
                    onClick={() => { setParentGameId(""); setSegmentLabel(""); }}
                    disabled={uploading}
                    style={{
                      padding: '6px 12px', background: 'transparent',
                      border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8,
                      color: '#bfc7d4', fontSize: 11, fontWeight: 600, cursor: uploading ? 'not-allowed' : 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >Start a new game instead</button>
                </div>
              )}
              {recentGames.length > 0 && (
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 10 }}>
                  {parentGameId ? "Or pick a different existing game:" : "Append to an existing multi-part game (optional)"}
                  <select
                    data-testid="parent-game-select"
                    className="input-field"
                    value={parentGameId}
                    onChange={e => { setParentGameId(e.target.value); setSegmentLabel(""); }}
                    disabled={uploading}
                  >
                    <option value="">— New multi-part game (start fresh) —</option>
                    {recentGames.map(g => (
                      <option key={g.id} value={g.id} disabled={g.multi_part_locked}>
                        {g.title} · {g.game_date} {(g.segments || []).length > 0 ? `· ${(g.segments || []).length} part(s)` : ""}{g.multi_part_locked ? " · COMPLETED 🔒" : ""}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {parentLocked && (
                <p data-testid="parent-locked-warning" style={{ fontSize: 12, color: '#ff9800', marginBottom: 10 }}>
                  This game has been marked complete. Re-open it from its watch page to add more segments.
                </p>
              )}
              <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block' }}>Which part is this video?
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                  {segmentOptions.map(opt => {
                    const used = usedSegmentLabels.has(opt.toLowerCase());
                    const blocked = disabledLabels.has(opt.toLowerCase());
                    return (
                      <button
                        key={opt}
                        type="button"
                        data-testid={`segment-label-${opt}`}
                        onClick={() => !blocked && setSegmentLabel(opt)}
                        disabled={uploading || blocked}
                        title={used ? "Already uploaded for this game" : blocked ? "Overlaps with a segment already uploaded" : ""}
                        style={{
                          padding: '8px 14px', cursor: blocked || uploading ? 'not-allowed' : 'pointer',
                          background: segmentLabel === opt ? 'var(--cyan)' : 'transparent',
                          border: '1px solid ' + (segmentLabel === opt ? 'var(--cyan)' : 'var(--dark-border)'),
                          borderRadius: 999, fontSize: 13, fontWeight: 700,
                          color: segmentLabel === opt ? '#000' : (blocked ? '#555' : '#fff'),
                          opacity: blocked ? 0.35 : 1,
                          fontFamily: "'DM Sans', sans-serif",
                          textDecoration: blocked && !used ? 'line-through' : 'none',
                        }}
                      >{opt}{used ? " ✓" : ""}</button>
                    );
                  })}
                </div>
              </label>
            </div>
          )}

          {/* File picker */}
          <div
            data-testid="game-upload-dropzone"
            onClick={() => fileRef.current && fileRef.current.click()}
            style={{
              padding: 28, border: '2px dashed var(--dark-border)', borderRadius: 12,
              background: file ? 'rgba(0,229,255,0.04)' : 'rgba(255,255,255,0.02)',
              textAlign: 'center', cursor: uploading ? 'default' : 'pointer',
              marginBottom: 18, transition: 'all 0.15s ease',
            }}
          >
            <input
              ref={fileRef}
              type="file"
              accept="video/mp4,video/quicktime"
              onChange={onPickFile}
              disabled={uploading}
              style={{ display: 'none' }}
              data-testid="game-upload-file"
            />
            {file ? (
              <>
                <Film size={32} style={{ color: 'var(--cyan)', marginBottom: 10 }} />
                <p style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{file.name}</p>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                  {(file.size / 1024 / 1024).toFixed(1)} MB{duration ? ` · ${Math.floor(duration/60)}m ${duration % 60}s` : ""}
                </p>
                {!uploading && (
                  <button
                    data-testid="game-upload-clear"
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setFile(null); setDuration(null); setProgress(0); if (fileRef.current) fileRef.current.value = ""; }}
                    style={{
                      marginTop: 10, padding: '4px 10px', background: 'transparent',
                      border: '1px solid var(--dark-border)', borderRadius: 8,
                      color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer',
                    }}
                  ><X size={12} style={{ verticalAlign: -2, marginRight: 4 }} />Remove</button>
                )}
              </>
            ) : (
              <>
                <Upload size={32} style={{ color: 'var(--cyan)', marginBottom: 10 }} />
                <p style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Click to pick a video</p>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>MP4 or MOV</p>
              </>
            )}
          </div>

          {/* Form — hidden when appending to an existing parent game (parent's metadata applies) */}
          {!(mode === "multi" && parentGameId) && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Title
              <input data-testid="game-upload-title" className="input-field" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} disabled={uploading} />
            </label>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Game date
              <input data-testid="game-upload-date" type="date" className="input-field" value={form.game_date} onChange={e => setForm({ ...form, game_date: e.target.value })} disabled={uploading} />
            </label>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Home team
              <TeamSearchSelect
                api={api}
                testId="game-upload-team"
                value={form.team_id}
                onChange={team_id => setForm(s => ({ ...s, team_id, away_team_id: s.away_team_id === team_id ? "" : s.away_team_id }))}
                disabled={uploading}
                placeholder="Type to find the home team…"
              />
            </label>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Away team (optional — pick to share with both teams)
              <TeamSearchSelect
                api={api}
                testId="game-upload-away-team"
                value={form.away_team_id}
                onChange={away_team_id => setForm(s => ({ ...s, away_team_id }))}
                disabled={uploading}
                excludeId={form.team_id}
                placeholder="Type to find the away team…"
                emptyLabel="No YSV team (use opponent name below)"
              />
            </label>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)', gridColumn: '1 / -1' }}>Opponent name (used when away team is not on YSV)
              <input data-testid="game-upload-opponent" className="input-field" value={form.opponent} onChange={e => setForm({ ...form, opponent: e.target.value })} disabled={uploading} placeholder="e.g., Chicago Hellcats — only needed if away team isn't on YSV" />
            </label>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)', gridColumn: '1 / -1' }}>Description
              <textarea data-testid="game-upload-desc" className="input-field" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} disabled={uploading} />
            </label>
          </div>
          )}

          {error && (
            <div data-testid="game-upload-error" style={{ marginTop: 12, padding: 10, background: 'rgba(244,67,54,0.12)', border: '1px solid rgba(244,67,54,0.3)', borderRadius: 10, color: '#f44336', fontSize: 13 }}>
              {error}
            </div>
          )}

          {uploading && (
            <div style={{ marginTop: 14 }}>
              <div style={{ height: 8, background: '#1a1a1f', borderRadius: 4, overflow: 'hidden' }}>
                <div data-testid="game-upload-progress" style={{ height: '100%', width: `${progress}%`, background: 'var(--cyan)', transition: 'width 0.2s' }} />
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> Uploading… {progress}%
              </p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button
              onClick={() => navigate("/games")}
              disabled={uploading}
              style={{
                flex: 1, padding: 12, background: 'transparent', border: '1px solid var(--dark-border)',
                borderRadius: 10, color: '#fff', fontWeight: 600, cursor: uploading ? 'default' : 'pointer',
                opacity: uploading ? 0.6 : 1,
              }}
            >Cancel</button>
            <button
              data-testid="game-upload-submit"
              onClick={submit}
              disabled={uploading || !file}
              className="btn-primary"
              style={{ flex: 2, opacity: (uploading || !file) ? 0.5 : 1 }}
            >
              {uploading ? `Uploading ${progress}%` : "Upload game"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameUploadPage;
